// @flow
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'semantic-ui-react';
import { errorLogger, infoLogger } from '../../helpers';
import { ToggleKeys } from '../../Constants';
import Header from 'views/Header';
import Hero from 'views/Hero';
import api from 'api';
import * as cssComviq from './LoginComviq.module.css';
import * as cssTele2 from './LoginTele2.module.css';
import DigitInput from './DigitInput';
import CardContainer from 'components/CardContainer';
import Footer from 'components/Footer';
import Card from 'components/Card';
import Loading, { T2Spinner, Spinner } from 'components';
import Error from 'views/Error';
import { useSelector } from 'react-redux';
import { isComviqCustomer } from '../../helpers/customerTypeHelpers';
import { getContentFor } from '../../helpers/contentHelpers';

type LoginProps = {
  setLoggedInStatus: () => void,
  setUserAuthData: () => void,
  msisdn: string,
  eid: string,
  iccid?: String,
  showPromotion?: boolean,
  isTransfer: boolean,
};

const Login = ({
  setLoggedInStatus,
  setUserAuthData,
  msisdn,
  eid,
  iccid,
  showPromotion = true,
  isTransfer = false,
}: LoginProps) => {
  const elements = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const [focusedElement, setFocusedElement] = useState(0);
  const [ssn, setSsn] = useState('');
  const [ssn1, setSsn1] = useState('');
  const [ssn2, setSsn2] = useState('');
  const [ssn3, setSsn3] = useState('');
  const [ssn4, setSsn4] = useState('');
  const [isDenied, setIsDenied] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [hasPromotion, setHasPromotion] = useState(false);

  const toggles = useSelector((state) => state.app.toggles);
  const loading = useSelector((state) => state.app.isLoading);
  const customerType = useSelector((state) => state.app.customerType);

  const allContent = getContentFor(customerType);
  const pageContent = allContent.page.login;
  const isComviq = isComviqCustomer(customerType);
  const css = isComviq ? cssComviq : cssTele2;

  const checkIfCustomerHasPromotion = () => {
    if (!customerType) return false;

    const toggleName = 'promotion-' + ToggleKeys.VENDOR[window.__VENDOR__] + customerType;

    return isToggleOn(toggleName);
  };

  const isToggleOn = (toggleName) => {
    if (!toggles) return false;

    const toggle = toggles
      .filter((toggle) => toggle && toggle.toggleId)
      .find((toggle) => toggle.toggleId.includes(toggleName));
    return !!toggle && toggle.isToggledOn;
  };

  useEffect(() => {
    if (elements[parseInt(focusedElement)].current) {
      elements[parseInt(focusedElement)].current.focus();
    }
    if ((ssn1.length > 0 && ssn2.length > 0 && ssn3.length > 0 && ssn4.length > 0) || ssn.length > 3) {
      setButtonDisabled(false);
    }
  }, [ssn1, ssn2, ssn3, ssn4, ssn]);

  const onChangeComviqInput = useCallback(
    (input) => {
      setFocusedElement(1);
      setSsn(input);
    },
    [setFocusedElement, setSsn]
  );

  const focusInput1 = useCallback(
    (digit) => {
      setFocusedElement(1);
      setSsn1(digit);
    },
    [setFocusedElement, setSsn1]
  );

  const focusInput2 = useCallback(
    (digit) => {
      setFocusedElement(2);
      setSsn2(digit);
    },
    [setFocusedElement, setSsn2]
  );

  const focusInput3 = useCallback(
    (digit) => {
      setFocusedElement(3);
      setSsn3(digit);
    },
    [setFocusedElement, setSsn3]
  );

  const focusInput4 = useCallback(
    (digit) => {
      setFocusedElement(4);
      setSsn4(digit);
    },
    [setSsn4]
  );

  const doLogin = useCallback(async () => {
    setIsLoggingIn(true);
    setButtonDisabled(true);
    const data = window.__POST_DATA__;
    let jsonWebToken;

    if (!!data && data !== 'undefined' && data !== 'null') {
      const newData = window.atob(data);
      jsonWebToken = JSON.parse(newData).carrierPostData;
    }
    try {
      // Login
      const loginPostData =
        ssn.length > 3 //Comviq
          ? {
            ssn1: ssn[0],
            ssn2: ssn[1],
            ssn3: ssn[2],
            ssn4: ssn[3],
            msisdn,
            eid,
            iccid,
            postData: data,
            token: jsonWebToken,
          }
          : {
            //Tele2
            ssn1,
            ssn2,
            ssn3,
            ssn4,
            msisdn,
            eid,
            iccid,
            postData: data,
            token: jsonWebToken,
          };

      const result = await api.login({ loginData: loginPostData, isTransfer });
      infoLogger({ name: 'client-do-login', log: 'User successfully logged in' });
      setUserAuthData(result.data);
      setLoggedInStatus(result.login);
    } catch (error) {
      errorLogger({
        name: 'client-do-login',
        log: `Login failed with error message: ${error.message} user entered ssn: ${ssn} ${ssn1}${ssn2}${ssn3}${ssn4}`,
        data: error.message,
      });

      setIsDenied(true);

      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage(
            'Något gick fel, var god försök igen vid ett senare tillfälle. Om felet kvarstår, kontakta kundtjänst.'
          );
        } else if (error.response.status === 401) {
          setErrorMessage(allContent.page.entitled.unautarizedError.htmlText);
        } else if (error.response.status === 403) {
          setErrorMessage('Fel 403 - Åtkomst nekad.');
        } else if (error.response.status === 429) {
          const t = error.response.data.bannedTimeRemaining;
          const days = `${Math.floor(t / (1000 * 60 * 60 * 24))} dag, ${Math.floor(
            (t / (1000 * 60 * 60)) % 24
          )} timmar och ${Math.floor((t / 1000 / 60) % 60)} minuter`;
          const hours = `${Math.floor((t / (1000 * 60 * 60)) % 24)} timmar och ${Math.floor(
            (t / 1000 / 60) % 60
          )} minuter`;
          const minutes = `${Math.floor((t / 1000 / 60) % 60)} minuter`;
          let timeRemaining;
          if (Math.floor(t / (1000 * 60 * 60 * 24)) > 0) {
            timeRemaining = days;
          } else if (Math.floor((t / (1000 * 60 * 60)) % 24) > 0) {
            timeRemaining = hours;
          } else {
            timeRemaining = minutes;
          }
          setErrorMessage(`${allContent.page.entitled.usageLimitError.htmlText} ${timeRemaining}.`);
        } else {
          setErrorMessage('Något gick fel, var god försök igen vid ett senare tillfälle.');
        }
      } else {
        setErrorMessage('Något gick fel, var god försök igen vid ett senare tillfälle.');
      }
    } finally {
      setIsLoggingIn(false);
      setButtonDisabled(false);
    }
  }, [isLoggingIn, isButtonDisabled]);

  useEffect(() => {
    if (errorMessage) {
      window.__PAGE__ = 'Login';
      infoLogger({ name: 'client-do-login', log: 'Ssn login failed with error message ' + errorMessage });
    }
  }, [errorMessage]);

  useEffect(() => {
    const promotion = checkIfCustomerHasPromotion();

    promotion && setHasPromotion(promotion);
  }, [customerType]);

  useEffect(() => {
    window.__PAGE__ = 'Login';
    infoLogger({ name: 'client-page-view-ssn-login', log: 'Ssn login page shown' });

    isComviq && setFocusedElement(0);
  }, []);

  if (loading || !css) return <Loading />;
  if (errorMessage) return <Error message={errorMessage} />;

  return (
    <div className={css.bg}>
      <Header />
      {isComviq && (
        <Hero
          title={pageContent.heroTitle}
          text={!isTransfer ? pageContent.heroText : ''}
          hasAvailablePromotion={showPromotion && hasPromotion}
          promotionText={'En fri månad'}
        />
      )}
      <CardContainer isComviq={isComviq}>
        <Card isComviq={isComviq}>
          <div className={css.container}>
            <h2 className={css.title}>{pageContent.title}</h2>
            <p className={css.text}>{pageContent.text}</p>
            {isLoggingIn && !isComviq && (
              <div className={css.loadingOverlay}>
                <T2Spinner />
                <div className={css.blurOverlay} />
              </div>
            )}

            <div className={css.inputFooterContainer}>
              {!isLoggingIn &&
                (isComviq ? (
                  <DigitInput isComviq ref={elements[0]} onHasDigit={onChangeComviqInput} maxLength={'4'} />
                ) : (
                  <Fragment>
                    <div className={css.numbers}>
                      <DigitInput ref={elements[0]} onHasDigit={focusInput1} />
                      <DigitInput ref={elements[1]} onHasDigit={focusInput2} />
                      <DigitInput ref={elements[2]} onHasDigit={focusInput3} />
                      <DigitInput ref={elements[3]} onHasDigit={focusInput4} />
                    </div>
                  </Fragment>
                ))}
              {isLoggingIn && isComviq && (
                <div className={css.loadingOverlay}>
                  <Spinner />
                  <div className={css.blurOverlay} />
                </div>
              )}
              <Footer isComviq={isComviq}>
                <Button className={css.button} color="blue" primary fluid disabled={isButtonDisabled} onClick={doLogin}>
                  {pageContent.button}
                </Button>
              </Footer>
            </div>
            {isDenied && <p>{errorMessage}</p>}
          </div>
        </Card>
        {isComviq && showPromotion && hasPromotion && !isTransfer && (
          <Card isComviq className={css.offerCard}>
            <div>
              <div className={css.offer}>En fri månad</div>
              <h4 className={`${css.title} ${css.titleOffer}`}>Första månaden kostnadsfri</h4>
              <p className={css.text}>
                Just nu bjuder vi på första månaden.
                <br />
                Därefter 45 kr/mån.
              </p>
              <p>Ingen bindningstid</p>
            </div>
          </Card>
        )}
      </CardContainer>
    </div>
  );
};

export default Login;
